import { useQuery } from '@apollo/client';
import { Delta } from 'quill/core';
import React, { useMemo } from 'react';
import { useParams } from 'react-router';
import * as styles from './ComparePostsView.module.scss';
import HoverContentPreview from './HoverContentPreview';
import { AssetTokenProvider } from '../editor/AssetTokenContext';
import QuillComponent from '../editor/QuillComponent';
import QuillContext from '../editor/QuillContext';
import { gql } from '../graphql/generated/gql';

const COMPARE_POSTS_VIEW_QUERY = gql(/** @complexity 4 */ `
  query ComparePostsView($basePostId: ID!, $comparePostId: ID!) {
    basePost: post(id: $basePostId) {
      id
      content(isLatest: true) {
        id
        delta
      }
    }
    comparePost: post(id: $comparePostId) {
      id
      content(isLatest: true) {
        id
        delta
      }
    }
  }
`);

function getDiff(baseContent: Delta, compareContent: Delta) {
  const diff = baseContent.diff(compareContent).map((op) => {
    if (op.insert) {
      return {
        ...op,
        attributes: { ...op.attributes, diff: 'inserted' },
      };
    }

    if (op.delete) {
      return {
        retain: op.delete,
        attributes: { ...op.attributes, diff: 'deleted' },
      };
    }

    return op;
  });

  return new Delta(diff);
}

const ComparePostsView = () => {
  const { basePostId, comparePostId } = useParams() as {
    basePostId: string;
    comparePostId: string;
  };

  const { data } = useQuery(COMPARE_POSTS_VIEW_QUERY, {
    fetchPolicy: 'network-only',
    variables: { basePostId, comparePostId },
  });

  const contentWithDiff = useMemo(() => {
    if (data?.comparePost && data?.basePost) {
      return new Delta(data.basePost.content.delta).compose(
        getDiff(
          new Delta(data.basePost.content.delta),
          new Delta(data.comparePost.content.delta),
        ),
      );
    }
    return null;
  }, [data]);

  if (contentWithDiff == null) return null;

  return (
    <div className={styles.container}>
      <QuillContext>
        <HoverContentPreview>
          <AssetTokenProvider entityAssetToken={{ token: '' }}>
            <QuillComponent
              rootClassName={styles.editorRoot}
              containerClassName={styles.editorContainer}
              namespace="history"
              initialContent={contentWithDiff}
              readOnly
            />
          </AssetTokenProvider>
        </HoverContentPreview>
      </QuillContext>
    </div>
  );
};

export default ComparePostsView;
